import { type DragEvent, type ReactNode, useState } from 'react'
import cx from 'classnames'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { SkeletonList } from '@/components/Skeleton'
import { QuestionCardSkeleton } from '@/features/legends/questions/components/QuestionCard'
import styles from './QuestionList.module.css'

export interface QuestionListProps {
  /**
   * The questions' cards.
   */
  children?: ReactNode
  isEmpty: boolean
  /**
   * Shown in place of the cards while there are none.
   */
  empty: ReactNode
  /**
   * Whether what is being dragged over the end of the list can be dropped
   * there.
   */
  canAcceptDrop: () => boolean
  onDrop: () => unknown
}

/**
 * A builder's questions as cards, which accepts drops at its end.
 */
export function QuestionList({
  children,
  isEmpty,
  empty,
  canAcceptDrop,
  onDrop,
}: QuestionListProps) {
  return (
    <div className={styles.root}>
      {isEmpty ? (
        <DropZone isFilled canAcceptDrop={canAcceptDrop} onDrop={onDrop}>
          {empty}
        </DropZone>
      ) : (
        <>
          {children}
          <DropZone canAcceptDrop={canAcceptDrop} onDrop={onDrop} />
        </>
      )}
    </div>
  )
}

/**
 * A skeleton for a {@link QuestionList}.
 */
export function QuestionListSkeleton() {
  return (
    <div className={styles.root}>
      <SkeletonList count={4}>
        <QuestionCardSkeleton />
      </SkeletonList>
    </div>
  )
}

/**
 * The hint of the legend's empty canvas.
 */
export function QuestionListEmptyState() {
  return (
    <EmptyState size="section">
      <EmptyStateIconDecoration icon="layers" />
      <EmptyStateTitle>Questions</EmptyStateTitle>
      <EmptyStateDescription>
        Start building the form by drag-dropping questions here, or click an
        element in the toolbox.
      </EmptyStateDescription>
    </EmptyState>
  )
}

/**
 * The hint of an empty path.
 */
export function QuestionListPathEmptyState() {
  return (
    <div className={styles.pathEmpty}>
      <p className={styles.pathEmptyTitle}>
        Drag questions in here to add them to this path.
      </p>
      <p className={styles.pathEmptyText}>
        Use the <strong className={styles.pathEmptyEmphasis}>Conditions</strong>{' '}
        tab to configure if the path should be triggered.
      </p>
    </div>
  )
}

interface DropZoneProps {
  children?: ReactNode
  /**
   * Holds the empty list's hint, rather than sitting after the cards.
   */
  isFilled?: boolean
  canAcceptDrop: () => boolean
  onDrop: () => unknown
}

function DropZone({
  children,
  isFilled,
  canAcceptDrop,
  onDrop,
}: DropZoneProps) {
  const [isOver, setIsOver] = useState(false)
  return (
    <div
      className={cx(
        isFilled ? styles.emptyZone : styles.endZone,
        isOver && styles.zoneOver,
      )}
      onDragOver={(e) => {
        if (!canAcceptDrop()) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        setIsOver(true)
      }}
      onDragLeave={(e) => !isWithin(e) && setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsOver(false)
        onDrop()
      }}
    >
      {children}
    </div>
  )
}

/**
 * Dragging over a child fires `dragleave` on the parent, which isn't leaving.
 */
function isWithin(e: DragEvent<HTMLElement>) {
  return e.currentTarget.contains(e.relatedTarget as Node | null)
}
